import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, UnstyledButton} from '@mantine/core';
import React, {useCallback, useEffect} from 'react';

import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Container} from 'ui-library/NYC';

import {
  type AccumulatingChatHistory,
  CHAT_HISTORY_PAGE_SIZE,
  STARRED_CHAT_HISTORY_PAGE_SIZE,
  useAccumulatingChatHistory,
} from '../ChatListing';
import type {SidepanelChat} from '../ChatRouter';
import RedesignChatRow, {type ChatStarUpdate} from './RedesignChatRow';

const useStyles = createStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    // Long chat titles ellipsis within the narrow sidebar; never scroll horizontally.
    overflowX: 'hidden',
  },
  // Minimized dropdown: flow at natural height (the dropdown itself scrolls).
  rootNatural: {
    height: 'auto',
    overflowY: 'visible',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  items: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  refreshing: {
    opacity: 0.5,
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#979799',
    margin: '18px 0 6px 4px',
  },
  viewMore: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '0 4px',
    fontSize: 13,
    lineHeight: '20px',
    color: '#3C65FB',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:disabled': {
      color: '#979799',
      cursor: 'default',
      textDecoration: 'none',
    },
  },
  emptyState: {
    padding: '20px 4px',
    color: '#979799',
    fontSize: 13,
  },
  emptyStateTitle: {
    color: '#1B1B1F',
    fontSize: 15,
    marginBottom: 4,
  },
}));

export interface GroupedChatListingProps {
  selectedChatSession: SidepanelChat | null;
  onSelectChatSession: (chat: SidepanelChat) => void;
  refetchRef: React.MutableRefObject<() => void>;
  /**
   * When true (sidebar), this listing scrolls. When false (minimized dropdown), it flows at its
   * natural height and the dropdown scrolls it.
   */
  fillHeight?: boolean;
}

/**
 * Redesigned chat history list (LLM_ENABLE_CHAT_REDESIGN): independently queries and paginates the
 * current user's starred and unstarred chats.
 */
interface ChatHistorySectionProps {
  history: AccumulatingChatHistory;
  renderItem: (item: AccumulatingChatHistory['items'][number]) => JSX.Element;
  showInitialLoading: boolean;
  testKey: 'starred' | 'recent';
  title: string;
}

function ChatHistorySection({
  history,
  renderItem,
  showInitialLoading,
  testKey,
  title,
}: ChatHistorySectionProps): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <div
      className={classes.section}
      {...getTestProps({component: 'GroupedChatListing', element: `${testKey}-section`})}
    >
      <div
        className={classes.sectionHeader}
        {...getTestProps({component: 'GroupedChatListing', element: `${testKey}-header`})}
      >
        {title}
      </div>
      <div
        aria-busy={history.isRefreshing}
        className={cx(classes.items, history.isRefreshing && classes.refreshing)}
        {...getTestProps({
          component: 'GroupedChatListing',
          element: `${testKey}-items`,
        })}
      >
        {history.isError ? (
          <Container size="sidepanel">
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text={`Error loading ${title.toLowerCase()} chats.`}
              action={{
                actionType: InlineBannerActionType.CUSTOM,
                actionText: 'Retry',
                actionOnClick: history.retry,
                rightAlign: true,
              }}
            />
          </Container>
        ) : (
          history.items.map((item) => renderItem(item))
        )}
        {showInitialLoading && <CenteredHorizontalThrobber />}
        {(history.hasMore || history.isLoadingMore) && (
          <UnstyledButton
            className={classes.viewMore}
            disabled={history.isLoadingMore}
            onClick={() => history.loadMore?.()}
            {...getTestProps({
              component: 'GroupedChatListing',
              element: `${testKey}-view-more`,
            })}
          >
            {history.isLoadingMore ? 'Loading…' : 'View more'}
          </UnstyledButton>
        )}
      </div>
    </div>
  );
}

export default function GroupedChatListing({
  selectedChatSession,
  onSelectChatSession,
  refetchRef,
  fillHeight = true,
}: GroupedChatListingProps): JSX.Element {
  const {classes, cx} = useStyles();
  const starredHistory = useAccumulatingChatHistory(true, STARRED_CHAT_HISTORY_PAGE_SIZE);
  const recentHistory = useAccumulatingChatHistory(false, CHAT_HISTORY_PAGE_SIZE);
  const refreshStarred = starredHistory.refresh;
  const refreshRecent = recentHistory.refresh;
  const removeRecentItem = recentHistory.removeItem;
  const removeStarredItem = starredHistory.removeItem;

  const refreshBoth = useCallback(async () => {
    await Promise.all([refreshStarred(), refreshRecent()]);
  }, [refreshRecent, refreshStarred]);

  useEffect(() => {
    refetchRef.current = refreshBoth;
  }, [refetchRef, refreshBoth]);

  const handleStarUpdated = useCallback(
    async ({chatId, isStarred}: ChatStarUpdate) => {
      if (isStarred) {
        removeRecentItem(chatId);
      } else {
        removeStarredItem(chatId);
      }
      await refreshBoth();
    },
    [refreshBoth, removeRecentItem, removeStarredItem]
  );

  const renderItem = useCallback(
    (item: AccumulatingChatHistory['items'][number]): JSX.Element => (
      <RedesignChatRow
        key={item.id ?? 'new-chat'}
        item={item}
        onStarUpdated={handleStarUpdated}
        onSelect={onSelectChatSession}
        isSelected={selectedChatSession?.id === item.id}
      />
    ),
    [handleStarUpdated, onSelectChatSession, selectedChatSession?.id]
  );

  const bothLoadedEmpty =
    !starredHistory.isInitialLoading &&
    !recentHistory.isInitialLoading &&
    !starredHistory.isError &&
    !recentHistory.isError &&
    starredHistory.items.length === 0 &&
    recentHistory.items.length === 0;
  if (bothLoadedEmpty) {
    return (
      <div
        className={classes.emptyState}
        {...getTestProps({component: 'GroupedChatListing', element: 'empty'})}
      >
        <p className={classes.emptyStateTitle}>No chats found</p>
        <p>Start a new chat from the navigation above.</p>
      </div>
    );
  }
  const showStarredSection = starredHistory.items.length > 0 || starredHistory.isError;

  return (
    <div
      className={cx(classes.root, fillHeight && 'scrollbar-override', !fillHeight && classes.rootNatural)}
      {...getTestProps({component: 'GroupedChatListing', element: 'root'})}
    >
      {showStarredSection && (
        <ChatHistorySection
          history={starredHistory}
          renderItem={renderItem}
          showInitialLoading={false}
          testKey="starred"
          title="Starred"
        />
      )}
      <ChatHistorySection
        history={recentHistory}
        renderItem={renderItem}
        showInitialLoading={starredHistory.isInitialLoading || recentHistory.isInitialLoading}
        testKey="recent"
        title="Recent"
      />
    </div>
  );
}
